<template>
  <div class="anamnesis-subset">
    <!-- Toolbar réutilisable -->
    <SubsetToolbar
      :sort-options="sortOptions"
      :sort-by="sortBy"
      :sort-direction="sortDirection"
      :is-card-view="isCardView"
      :search-query="searchQuery"
      search-placeholder="Rechercher une entité numérique..."
      create-button-text="Nouvelle entité"
      :show-create-button="true"
      :show-mobile-menu="showMobileMenu"
      @update:sort-by="handleUpdateSortBy"
      @update:search-query="handleUpdateSearchQuery"
      @toggle-sort-direction="handleToggleSortDirection"
      @toggle-view="handleToggleView"
      @toggle-mobile-menu="handleToggleMobileMenu"
      @create="$emit('create')"
    />

    <!-- Liste des entités numériques -->
    <div v-if="paginatedEntities.length > 0" :class="{ 'anamnesis-grid': isCardView, 'anamnesis-table': !isCardView }">
      <!-- En-tête du tableau (mode liste seulement) -->
      <div v-if="!isCardView" class="flex items-center px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
        <div class="flex-[1.25] min-w-0">Entité numérique</div>
        <div class="hidden md:flex flex-1 min-w-0 px-3">Propriétaire</div>
        <div class="hidden lg:flex flex-1 min-w-0 px-3">Copyright</div>
        <div class="min-w-0 px-3">Photos</div>
      </div>
      
      <DigitalEntityCard
        v-for="entity in paginatedEntities"
        :key="entity.uuid"
        :digital-entity="entity"
        :is-input="entity.is_input"
        :view-mode="isCardView ? 'default' : 'table'"
        :show-actions="showActions"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :is-owned="isDigitalEntityOwned(entity)"
        :is-public="entity.is_public"
        :show-ownership-badge="true"
        :user-id="entity.user_id"
        @click="handleView(entity)"
        @edit="$emit('edit', entity)"
        @delete="$emit('delete', entity)"
      />
    </div>

    <div v-else class="text-center py-8">
      <p class="text-gray-500">Aucune entité numérique trouvée</p>
    </div>

    <!-- Compteur d'entités -->
    <div class="flex justify-end mt-4 mb-2">
      <p class="text-sm text-gray-400">
        {{ filteredEntities.length }} entité{{ filteredEntities.length > 1 ? 's' : '' }} numérique{{ filteredEntities.length > 1 ? 's' : '' }} trouvée{{ filteredEntities.length > 1 ? 's' : '' }}
      </p>
    </div>

    <!-- Pagination -->
    <div v-if="filteredEntities.length > itemsPerPage" class="mt-4 flex justify-center space-x-2">
      <button
        :disabled="currentPage === 1"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handlePreviousPage"
      >
        Précédent
      </button>
      <button
        v-for="page in totalPages"
        :key="page"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="currentPage = page"
      >
        {{ page }}
      </button>
      <button
        :disabled="currentPage === totalPages"
        class="px-3 py-1 rounded-md border"
        :class="currentPage === totalPages ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="handleNextPage"
      >
        Suivant
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { DigitalEntity } from '../../api/types/entities/digitalEntity/types';
import { useAuthStore } from '../../stores/auth';
import type { SortOption } from '../UI/SubsetToolbar.vue';
import SubsetToolbar from '../UI/SubsetToolbar.vue';
import DigitalEntityCard from '../cards/DigitalEntityCard.vue';

// Interface pour les entités enrichies
export interface EnhancedDigitalEntity extends DigitalEntity {
  is_input?: boolean;
}

// Types stricts
type SortBy = 'date' | 'appellation' | 'type' | 'format' | 'owner';
type SortDirection = 'asc' | 'desc';

// Props avec types stricts
interface Props {
  entities: EnhancedDigitalEntity[];
  showActions?: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  itemsPerPage?: number;
  defaultSort?: SortBy;
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showEditButton: true,
  showDeleteButton: true,
  itemsPerPage: 20,
  defaultSort: 'date',
});

// Events avec types stricts
defineEmits<{
  view: [entity: EnhancedDigitalEntity];
  create: [];
  edit: [entity: EnhancedDigitalEntity];
  delete: [entity: EnhancedDigitalEntity];
}>();

// Store auth
const authStore = useAuthStore();

// Router pour la navigation
const router = useRouter();

// État réactif avec types stricts
const isCardView: Ref<boolean> = ref(true);
const searchQuery: Ref<string> = ref('');
const sortBy: Ref<SortBy> = ref(props.defaultSort);
const sortDirection: Ref<SortDirection> = ref('desc');
const currentPage: Ref<number> = ref(1);
const showMobileMenu: Ref<boolean> = ref(false);

// Options de tri avec types stricts
const sortOptions: ComputedRef<SortOption[]> = computed(() => [
  { value: 'date', label: 'Date' },
  { value: 'appellation', label: 'Nom' },
  { value: 'type', label: 'Type' },
  { value: 'format', label: 'Format' },
  { value: 'owner', label: 'Propriétaire' },
]);

// Handlers avec types de retour explicites
const handleUpdateSortBy = (value: string): void => {
  sortBy.value = value as SortBy;
};

const handleUpdateSearchQuery = (value: string): void => {
  searchQuery.value = value;
};

const handleToggleSortDirection = (): void => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
};

const handleToggleView = (): void => {
  isCardView.value = !isCardView.value;
};

const handleToggleMobileMenu = (): void => {
  showMobileMenu.value = !showMobileMenu.value;
};

const handlePreviousPage = (): void => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const handleNextPage = (): void => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Fonction pour vérifier la propriété d'une entité numérique
const isDigitalEntityOwned = (entity: EnhancedDigitalEntity): boolean => {
  return authStore.isAssetOwner(entity);
};

// Fonction de recherche typée correctement
const searchInEntity = (entity: EnhancedDigitalEntity, query: string): boolean => {
  const searchTerm = query.toLowerCase();
  
  // Recherche dans les champs principaux
  if (entity.appellation?.toLowerCase().includes(searchTerm)) return true;
  if (entity.description?.toLowerCase().includes(searchTerm)) return true;
  if (entity.type?.toLowerCase().includes(searchTerm)) return true;
  if (entity.format?.toLowerCase().includes(searchTerm)) return true;
  if (entity.owner?.toLowerCase().includes(searchTerm)) return true;
  
  // Recherche dans l'UUID
  if (entity.uuid?.toLowerCase().includes(searchTerm)) return true;
  
  return false;
};

// Filtrage et tri avec types stricts
const filteredEntities: ComputedRef<EnhancedDigitalEntity[]> = computed(() => {
  let result: EnhancedDigitalEntity[] = [...props.entities];

  // Filtrage par recherche
  if (searchQuery.value) {
    result = result.filter((entity: EnhancedDigitalEntity): boolean => 
      searchInEntity(entity, searchQuery.value)
    );
  }

  // Tri
  result.sort((a: EnhancedDigitalEntity, b: EnhancedDigitalEntity): number => {
    let comparison: number = 0;
    
    switch (sortBy.value) {
      case 'date':
        comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        break;
      case 'appellation':
        comparison = (a.appellation || '').localeCompare(b.appellation || '');
        break;
      case 'type':
        comparison = (a.type || '').localeCompare(b.type || '');
        break;
      case 'format':
        comparison = (a.format || '').localeCompare(b.format || '');
        break;
      case 'owner':
        comparison = (a.owner || '').localeCompare(b.owner || '');
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison * -1 : comparison;
  });

  return result;
});

// Pagination avec types stricts
const totalPages: ComputedRef<number> = computed(() => 
  Math.ceil(filteredEntities.value.length / props.itemsPerPage)
);

const paginatedEntities: ComputedRef<EnhancedDigitalEntity[]> = computed(() => {
  const start: number = (currentPage.value - 1) * props.itemsPerPage;
  const end: number = start + props.itemsPerPage;
  return filteredEntities.value.slice(start, end);
});

// Navigation vers le détail
const handleView = (entity: EnhancedDigitalEntity): void => {
  router.push(`/digitalentity/${entity.uuid}`);
};

// Watchers avec types stricts
watch([searchQuery, sortBy], (): void => {
  currentPage.value = 1;
});
</script>

<style scoped>
.anamnesis-subset {
  width: 100%;
}

.anamnesis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.anamnesis-table {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  background: white;
}
</style> 